import { apiService } from './api';
import { CreateReceiptDto, ReceiptDto, UpdateReceiptDto } from '../types/receipt';

export const receiptService = {
  // Create a new receipt
  async createReceipt(data: CreateReceiptDto): Promise<ReceiptDto> {
    console.log('Creating receipt with data:', data);
    try {
      const response = await apiService.post('/receipts', data);
      console.log('Receipt created successfully:', response);
      return response.receipt;
    } catch (error: any) {
      console.error('Failed to create receipt:', error);
      console.error('Error response:', error?.response?.data);
      throw error;
    }
  },

  // Get receipt by ID
  async getReceipt(id: string): Promise<ReceiptDto> {
    const response = await apiService.get(`/receipts/${id}`);
    return response.receipt;
  },

  // Get receipt by payment ID
  async getReceiptByPaymentId(paymentId: string): Promise<ReceiptDto | null> {
    try {
      const response = await apiService.get(`/receipts/payment/${paymentId}`);
      return response.receipt;
    } catch (error: any) {
      // Check both error.status (normalized by apiService) and error.response?.status
      if (error.status === 404 || error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  // List receipts with pagination and filters
  async getReceipts(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    paymentId?: string;
  }) {
    const response = await apiService.get('/receipts', { params });
    return {
      receipts: response.receipts || [],
      pagination: response.pagination,
    };
  },

  // Update a receipt
  async updateReceipt(id: string, data: UpdateReceiptDto): Promise<ReceiptDto> {
    const response = await apiService.put(`/receipts/${id}`, data);
    return response.receipt;
  },

  // Delete a receipt
  async deleteReceipt(id: string): Promise<void> {
    await apiService.delete(`/receipts/${id}`);
  },

  // Generate a receipt for a payment
  async generateReceipt(paymentId: string, notes?: string): Promise<ReceiptDto> {
    const receiptNumber = await this.generateReceiptNumber();
    return this.createReceipt({
      paymentId,
      receiptNumber,
      notes,
      status: 'ACTIVE',
    });
  },

  // Generate a receipt number (client-side, will be validated by the server)
  async generateReceiptNumber(): Promise<string> {
    const date = new Date();
    const prefix = `RCPT-${date.getFullYear()}${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}`;

    // Get the latest receipt number from the server
    try {
      const response = await apiService.get('/receipts', {
        params: {
          limit: 1,
          sortBy: 'receiptNumber',
          sortOrder: 'desc',
        },
      });

      if (response.receipts && response.receipts.length > 0) {
        const lastReceipt = response.receipts[0];
        if (lastReceipt.receiptNumber.startsWith(prefix)) {
          const lastSequence = parseInt(lastReceipt.receiptNumber.split('-').pop() || '0', 10);
          return `${prefix}-${(lastSequence + 1).toString().padStart(4, '0')}`;
        }
      }
    } catch (error) {
      console.error('Failed to generate receipt number, using fallback', error);
    }

    // Fallback if API call fails
    return `${prefix}-0001`;
  },

  // Build receipt HTML for printing
  buildReceiptHtml(receipt: any): string {
    const payment = receipt.payment || {};
    const client = payment.client || {};
    const diet = client.diet || {};

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Receipt #${receipt.receiptNumber}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
    }
    .header {
      text-align: center;
      margin-bottom: 30px;
      border-bottom: 2px solid #333;
      padding-bottom: 20px;
    }
    .header h1 {
      margin: 0;
      color: #333;
    }
    .receipt-number {
      font-size: 18px;
      color: #666;
      margin-top: 10px;
    }
    .section {
      margin-bottom: 20px;
    }
    .section-title {
      font-weight: bold;
      font-size: 16px;
      margin-bottom: 10px;
      color: #333;
    }
    .info-row {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid #eee;
    }
    .info-label {
      font-weight: bold;
      color: #666;
    }
    .info-value {
      color: #333;
    }
    .total {
      font-size: 20px;
      font-weight: bold;
      margin-top: 20px;
      padding: 15px;
      background-color: #f5f5f5;
      border-radius: 5px;
    }
    .footer {
      margin-top: 40px;
      text-align: center;
      color: #999;
      font-size: 12px;
      border-top: 1px solid #eee;
      padding-top: 20px;
    }
    @media print {
      body {
        padding: 0;
      }
      .no-print {
        display: none;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>PROBOXLB</h1>
    <div class="receipt-number">Receipt #${receipt.receiptNumber}</div>
    <div style="margin-top: 10px; color: #666;">
      Issued: ${new Date(receipt.issuedAt).toLocaleDateString()}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Client Information</div>
    <div class="info-row">
      <span class="info-label">Name:</span>
      <span class="info-value">${client.firstName || ''} ${client.lastName || ''}</span>
    </div>
    <div class="info-row">
      <span class="info-label">Email:</span>
      <span class="info-value">${client.email || '-'}</span>
    </div>
    <div class="info-row">
      <span class="info-label">Diet Plan:</span>
      <span class="info-value">${diet.name || '-'}</span>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Payment Details</div>
    <div class="info-row">
      <span class="info-label">Payment Method:</span>
      <span class="info-value">${payment.method || '-'}</span>
    </div>
    <div class="info-row">
      <span class="info-label">Status:</span>
      <span class="info-value">${payment.status || '-'}</span>
    </div>
    <div class="info-row">
      <span class="info-label">Transaction ID:</span>
      <span class="info-value">${payment.transactionId || '-'}</span>
    </div>
    ${payment.notes ? `
    <div class="info-row">
      <span class="info-label">Notes:</span>
      <span class="info-value">${payment.notes}</span>
    </div>
    ` : ''}
  </div>

  <div class="total">
    <div style="display: flex; justify-content: space-between;">
      <span>Total Amount:</span>
      <span>${payment.currency || 'USD'} ${(payment.amount || 0).toFixed(2)}</span>
    </div>
  </div>

  ${receipt.notes ? `
  <div class="section">
    <div class="section-title">Receipt Notes</div>
    <div style="padding: 10px; background-color: #f9f9f9; border-radius: 5px;">
      ${receipt.notes}
    </div>
  </div>
  ` : ''}

  <div class="footer">
    <p>Thank you for your business!</p>
    <p>Printed on ${new Date().toLocaleString()}</p>
  </div>
</body>
</html>`;
  },

  // Print receipt in a new window
  async printReceipt(id: string): Promise<void> {
    const receipt = await this.getReceipt(id);
    const html = this.buildReceiptHtml(receipt);
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Unable to open print window. Please allow popups for this site.');
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    // Wait for content to load before printing
    printWindow.onload = () => {
      printWindow.print();
    };
  },

  // Download receipt as HTML file
  async downloadReceipt(id: string): Promise<void> {
    const receipt = await this.getReceipt(id);
    const html = this.buildReceiptHtml(receipt);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt-${receipt.receiptNumber || id}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
};
